import { dictionaryApi } from './dictionaryClient';
import type { Language } from './languageApi';
import type { SutraCommentary } from './sutraCommentaryApi';

export type SutraCommentaryText = {
  id: number;
  sutraId: number;
  text: string;
  sutraCommentaryId: number;
  languageId: number;
  sutraCommentary: SutraCommentary | null;
  language: Language | null;
};

export type CreateSutraCommentaryTextBody = {
  sutraId: number;
  text: string;
  sutraCommentaryId: number;
  languageId: number;
};

export type UpdateSutraCommentaryTextBody = Partial<CreateSutraCommentaryTextBody>;

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function num(o: Record<string, unknown>, key: string): number | null {
  const v = o[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function str(o: Record<string, unknown>, key: string): string {
  if (!(key in o) || o[key] == null) return '';
  return String(o[key]);
}

export function normalizeSutraCommentaryText(raw: unknown): SutraCommentaryText | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const sutraId = num(raw, 'sutraId') ?? num(raw, 'sutra_id');
  const sutraCommentaryId = num(raw, 'sutraCommentaryId') ?? num(raw, 'sutra_commentary_id');
  const languageId = num(raw, 'languageId') ?? num(raw, 'language_id');
  if (id == null || sutraId == null || sutraCommentaryId == null || languageId == null) return null;
  const commentaryRaw = raw.sutraCommentary ?? raw.sutra_commentary;
  const languageRaw = raw.language;
  const sutraCommentary =
    isRecord(commentaryRaw) && num(commentaryRaw, 'id') != null
      ? { id: num(commentaryRaw, 'id')!, name: str(commentaryRaw, 'name') }
      : null;
  const language =
    isRecord(languageRaw) && num(languageRaw, 'id') != null
      ? {
          id: num(languageRaw, 'id')!,
          name: str(languageRaw, 'name'),
          code: str(languageRaw, 'code'),
        }
      : null;
  return {
    id,
    sutraId,
    text: str(raw, 'text'),
    sutraCommentaryId,
    languageId,
    sutraCommentary,
    language,
  };
}

function normalizeList(data: unknown): SutraCommentaryText[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows.map(normalizeSutraCommentaryText).filter((x): x is SutraCommentaryText => x != null);
}

export async function listSutraCommentaryText(sutraId?: number): Promise<SutraCommentaryText[]> {
  const hasSutraId = sutraId != null && Number.isFinite(sutraId);
  const res = hasSutraId
    ? await dictionaryApi.get<unknown>(`/sutra-commentary-text/sutra/${sutraId}`)
    : await dictionaryApi.get<unknown>('/sutra-commentary-text');
  return normalizeList(res.data);
}

export async function getSutraCommentaryText(id: number): Promise<SutraCommentaryText> {
  const res = await dictionaryApi.get<unknown>(`/sutra-commentary-text/${id}`);
  const row = normalizeSutraCommentaryText(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid sutra commentary text response');
  return row;
}

export async function createSutraCommentaryText(
  body: CreateSutraCommentaryTextBody
): Promise<SutraCommentaryText> {
  const res = await dictionaryApi.post<unknown>('/sutra-commentary-text', body);
  const row = normalizeSutraCommentaryText(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid sutra commentary text response');
  return row;
}

export async function updateSutraCommentaryText(
  id: number,
  body: UpdateSutraCommentaryTextBody
): Promise<SutraCommentaryText> {
  const res = await dictionaryApi.patch<unknown>(`/sutra-commentary-text/${id}`, body);
  const row = normalizeSutraCommentaryText(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid sutra commentary text response');
  return row;
}

export async function deleteSutraCommentaryText(id: number): Promise<void> {
  await dictionaryApi.delete(`/sutra-commentary-text/${id}`);
}
